import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { FilterGroup } from './filter-group';
import { FilterItemType } from '../enums/filter-item-type';

export class FilterItem {

  readonly Group: FilterGroup;
  readonly ItemType: FilterItemType | string;
  readonly Name: string;
  readonly IsSelectedByDefault: boolean;
  readonly IsAlwaysVisible: boolean;
  readonly IsAlwaysSelected: boolean;
  readonly IsNeverVisible: boolean;
  readonly IsAboveFold: boolean;

  private count: number;
  private countSubject = new ReplaySubject<number>(1);
  readonly Count$ = this.countSubject.asObservable();
  get Count(): number {
    return this.count;
  }

  private isSelected = false;
  private isSelectedSubject = new BehaviorSubject<boolean>(this.isSelected);
  readonly IsSelected$ = this.isSelectedSubject.asObservable();
  get IsSelected(): boolean {
    return this.isSelected;
  }

  private value: string | number | Date = null;
  private valueSubject = new BehaviorSubject<string | number | Date>(this.value);
  readonly Value$ = this.valueSubject.asObservable();
  get Value(): string | number | Date {
    return this.value;
  }

  private isItemVisible: boolean;
  private isItemVisibleSubject = new ReplaySubject<boolean>(1);
  readonly IsItemVisible$ = this.isItemVisibleSubject.asObservable();
  get IsItemVisible(): boolean {
    return this.isItemVisible;
  }

  private isCountVisible = true;
  private isCountVisibleSubject = new BehaviorSubject<boolean>(this.isCountVisible);
  readonly IsCountVisible$ = this.isCountVisibleSubject.asObservable();
  get IsCountVisible(): boolean {
    return this.isCountVisible;
  }

  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string);
  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault: boolean);
  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault: boolean, isAlwaysSelected: boolean);
  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault: boolean, isAlwaysSelected: boolean, isAlwaysVisible: boolean);
  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault: boolean, isAlwaysSelected: boolean, isAlwaysVisible: boolean, isNeverVisible: boolean);

  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault: boolean, isAlwaysSelected: boolean, isAlwaysVisible: boolean, isNeverVisible: boolean, isAboveFold: boolean);
  constructor(group: FilterGroup, itemType: FilterItemType | string, name: string, isSelectedByDefault = false, isAlwaysSelected = false, isAlwaysVisible = false, isNeverVisible = false, isAboveFold = true) {
    this.Group = group;
    this.ItemType = itemType;
    this.Name = name;
    this.IsSelectedByDefault = isSelectedByDefault;
    if (this.IsSelectedByDefault) {
      this.setIsSelected(true);
    }
    this.IsAlwaysSelected = isAlwaysSelected;
    if (this.IsAlwaysSelected) {
      this.setIsSelected(true);
    }
    this.IsAlwaysVisible = isAlwaysVisible;
    if (this.IsAlwaysVisible) {
      this.setIsItemVisible(true);
    }
    this.IsNeverVisible = isNeverVisible;
    if (this.IsNeverVisible) {
      this.setIsItemVisible(false);
    }
    this.IsAboveFold = isAboveFold;
  }

  setCount(count: number): void {
    this.count = count;
    this.countSubject.next(this.count);
  }

  setValue(value: string | number | Date | null): void {
    this.value = value;
    this.valueSubject.next(this.value);
  }

  toggleSelection(): void {
    this.Group.setIsActive(true);
    this.setIsSelected(!this.isSelected);
    this.Group.raiseFiltersChanged(this);
  }

  switchSelection(): void {
    this.Group.setIsActive(true);
    this.setIsSelected(!this.isSelected);
    this.Group.Items.filter(i => i.ItemType !== this.ItemType).forEach(i => i.setIsSelected(false));
    this.Group.raiseFiltersChanged(...this.Group.Items);
  }

  setIsSelected(isSelected: boolean): void {
    if (this.IsAlwaysSelected) {
      this.isSelected = true;
    } else {
      this.isSelected = isSelected;
    }
    this.isSelectedSubject.next(this.isSelected);
  }

  setIsItemVisible(isVisible: boolean): void {
    if (this.IsAlwaysVisible) {
      this.isItemVisible = true;
    } else if (this.IsNeverVisible) {
      this.isItemVisible = false;
    } else {
      this.isItemVisible = isVisible;
    }
    this.isItemVisibleSubject.next(this.isItemVisible);
  }

  setIsCountVisible(isVisible: boolean): void {
    if (this.IsNeverVisible) {
      this.isCountVisible = false;
    } else {
      this.isCountVisible = isVisible;
    }
    this.isCountVisibleSubject.next(this.isCountVisible);
  }

  reset(): void {
    if (!this.IsAlwaysSelected) {
      this.setValue(null);
    }
    this.setIsSelected(false);
  }
}
